import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import common from '@/shared/i18n/en/common';
import { tr } from '@/shared/i18n/translate';

import { onLiveNotification } from './live-notification-bus';

/**
 * Surfaces important notifications as a live, dismissible in-app toast the
 * moment they arrive over SSE — so a mention / assignment / status change /
 * deadline "flashes" while you're working instead of only sitting in the
 * inbox. Clicking opens the inbox to act on it.
 *
 * Restraint (so it never feels like spam):
 *   - only `important` kinds interrupt (curated server-side in the presenter);
 *   - the same notification never toasts twice (dedup by id);
 *   - copy comes from the shared presenter, so the toast, the browser push,
 *     and the future native push all say the same thing.
 *
 * Mount once at the app root (alongside `useRealtimeStream`).
 */
export function useLiveNotificationToasts(opts: { enabled: boolean }): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!opts.enabled) return;

    // Bounded LRU-ish set so reconnect replays / rapid re-fires don't double-toast.
    const seen = new Set<string>();

    return onLiveNotification((n) => {
      if (!n.important) return;
      if (n.id) {
        if (seen.has(n.id)) return;
        seen.add(n.id);
        if (seen.size > 100) seen.delete(seen.values().next().value as string);
      }

      toast(n.title, {
        description: n.body || undefined,
        // Deep-links resolve through the inbox for now; navigate there so the
        // user lands where they can open the exact item.
        action: {
          label: tr(common, 'open'),
          onClick: () => void navigate({ to: '/inbox' }),
        },
        duration: 6000,
      });
    });
  }, [opts.enabled, navigate]);
}
